import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import {
    BarChart2, Users, CheckCircle2, Circle, Tag, LogOut,
    RefreshCw, Search, ChevronRight, ChevronLeft, Wifi,
    LayoutDashboard, List, Layers, Lock, Mail, Eye, EyeOff,
    TrendingUp, UserCheck, Clock, Award, X, ArrowLeftRight
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL;
const STORAGE_KEY = 'v2ScanAuth';

// ── Login ──────────────────────────────────────────────────────────────────────
const LoginScreen = ({ onLogin }) => {
    const [form, setForm] = useState({ username: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPw, setShowPw] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API}/api/v2/scan/login`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form)
            });
            const data = await res.json();
            if (data.success) {
                const auth = { username: form.username, password: form.password, role: data.role || 'full', eventInfo: { id: data.eventId, name: data.eventName } };
                localStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
                onLogin(auth);
            } else setError(data.message || 'Invalid credentials');
        } catch { setError('Cannot connect to server'); }
        finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <div className="w-full max-w-sm">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-indigo-600 mb-4 shadow-lg shadow-indigo-200">
                        <LayoutDashboard className="w-7 h-7 text-white" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900">Event Dashboard</h1>
                    <p className="text-sm text-gray-500 mt-1">Sign in with event credentials</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Username</label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input type="text" required value={form.username}
                                    onChange={e => setForm(p => ({ ...p, username: e.target.value }))}
                                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    placeholder="event-username" />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Password</label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input type={showPw ? 'text' : 'password'} required value={form.password}
                                    onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                                    className="w-full pl-10 pr-10 py-2.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                                <button type="button" onClick={() => setShowPw(!showPw)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        {error && (
                            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                                <X className="w-3.5 h-3.5 text-red-500 shrink-0" />
                                <p className="text-xs text-red-600">{error}</p>
                            </div>
                        )}
                        <button type="submit" disabled={loading}
                            className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm rounded-lg disabled:opacity-50 transition-colors">
                            {loading ? 'Signing in...' : 'Sign In'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

// ── KPI Card ───────────────────────────────────────────────────────────────────
const KPI = ({ label, value, sub, icon: Icon, color }) => {
    const cfg = {
        indigo: { bg: 'bg-indigo-50',  icon: 'text-indigo-600', border: 'border-indigo-100' },
        green:  { bg: 'bg-emerald-50', icon: 'text-emerald-600', border: 'border-emerald-100' },
        amber:  { bg: 'bg-amber-50',   icon: 'text-amber-600',  border: 'border-amber-100' },
        slate:  { bg: 'bg-gray-100',   icon: 'text-gray-500',   border: 'border-gray-200' },
    }[color || 'indigo'];
    return (
        <div className={`rounded-xl border ${cfg.border} bg-white p-4 flex items-start gap-3 shadow-sm`}>
            <div className={`p-2 rounded-lg ${cfg.bg} shrink-0`}>
                <Icon className={`w-4 h-4 ${cfg.icon}`} />
            </div>
            <div className="min-w-0">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider truncate">{label}</p>
                <p className="text-2xl font-black text-gray-900 mt-0.5 leading-none">{value}</p>
                {sub && <p className="text-[11px] text-gray-400 mt-1">{sub}</p>}
            </div>
        </div>
    );
};

// ── Progress Bar ───────────────────────────────────────────────────────────────
const Bar = ({ value, max, color = 'indigo' }) => {
    const pct = max > 0 ? Math.round((value / max) * 100) : 0;
    const colors = { indigo: 'bg-indigo-500', green: 'bg-emerald-500', amber: 'bg-amber-400' };
    return (
        <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                <div className={`${colors[color]} h-1.5 rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
            </div>
            <span className="text-[10px] text-gray-400 font-mono w-8 text-right">{pct}%</span>
        </div>
    );
};

// ── Overview Tab (single-screen banner layout) ─────────────────────────────────
const OverviewTab = ({ credentials, onStats, showEmpty }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lastRefresh, setLastRefresh] = useState(null);
    const timerRef = useRef(null);

    const fetch_ = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const res = await fetch(`${API}/api/v2/scan/stats`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(credentials)
            });
            const data = await res.json();
            if (data.success) {
                setStats(data.data);
                setLastRefresh(new Date());
                onStats?.(data.data);
            }
        } catch {}
        finally { setLoading(false); }
    }, [credentials, onStats]);

    useEffect(() => {
        fetch_();
        timerRef.current = setInterval(() => fetch_(true), 5000);
        return () => clearInterval(timerRef.current);
    }, [fetch_]);

    if (loading && !stats) return (
        <div className="flex items-center justify-center h-full">
            <RefreshCw className="w-6 h-6 text-indigo-500 animate-spin" />
        </div>
    );
    if (!stats) return null;

    const categories = stats.categories || [];
    const uncategorized = stats.uncategorized || { total: 0, checkedIn: 0 };
    const allCats = [
        ...categories,
        ...(uncategorized.total > 0
            ? [{ id: '__none__', name: 'Uncategorized', total: uncategorized.total, checkedIn: uncategorized.checkedIn }]
            : [])
    ];

    return (
        <div className="h-full flex gap-3">

            {/* ── LEFT: Big live check-in counter ── */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm flex flex-col items-center justify-center text-center px-6"
                style={{ flex: '0 0 20%' }}>
                <div className="flex items-center gap-2 mb-4">
                    <span className="flex h-3.5 w-3.5 relative">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-emerald-500" />
                    </span>
                    <span className="text-base font-extrabold text-gray-700 uppercase tracking-widest">Live Check-ins</span>
                </div>
                <span className="font-black text-gray-900 tracking-tighter leading-none"
                    style={{ fontSize: 'clamp(6rem, 11vw, 14rem)' }}>
                    {stats.checkedIn || 0}
                </span>
                {lastRefresh && (
                    <span className="text-xs text-gray-400 flex items-center gap-1 mt-5">
                        <Wifi className="w-3 h-3" /> {lastRefresh.toLocaleTimeString()}
                    </span>
                )}
            </div>

            {/* ── RIGHT: Category grid ── */}
            <div className="flex-1 min-h-0 flex flex-col gap-2">
                <p className="text-sm font-extrabold text-gray-600 uppercase tracking-widest shrink-0 pl-0.5">
                    Check-ins by Category
                </p>
                <div className="flex-1 min-h-0 grid gap-2"
                    style={{
                        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                        gridAutoRows: 'minmax(0, 1fr)'
                    }}>
                    {allCats.filter(cat => cat.total >= 4 && (showEmpty ? true : cat.checkedIn > 0)).map(cat => {
                        const pct = Math.round((cat.checkedIn / cat.total) * 100);
                        return (
                            <div key={cat.id}
                                className="bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col items-center justify-center text-center px-3 py-2 overflow-hidden">
                                <p className="text-xs font-extrabold text-gray-600 uppercase tracking-wide leading-tight line-clamp-2 mb-1">
                                    {cat.name}
                                </p>
                                <span className="font-black text-gray-900 tracking-tighter leading-none"
                                    style={{ fontSize: 'clamp(2.5rem, 4vw, 5rem)' }}>
                                    {cat.checkedIn}
                                </span>
                                <span className="text-sm font-semibold text-gray-500 mt-1">/ {cat.total}</span>
                                <div className="w-full bg-gray-100 rounded-full mt-2" style={{ height: '4px' }}>
                                    <div className="bg-emerald-500 rounded-full h-full transition-all duration-700"
                                        style={{ width: `${pct}%` }} />
                                </div>
                                <span className="text-xs font-bold text-gray-500 mt-1">{pct}%</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// ── Resend Mail Cell ───────────────────────────────────────────────────────────
const ResendMailCell = ({ p, fields, credentials, emailTemplates }) => {
    const [open, setOpen] = useState(false);
    const [dropPos, setDropPos] = useState({ top: 0, right: 0 });
    const [sending, setSending] = useState(false);
    const btnRef = useRef(null);
    const dropRef = useRef(null);

    const emailField = fields.find(f => f.fieldType === 'email' || f.fieldName?.toLowerCase().includes('email'));
    const participantEmail = emailField
        ? (p.data[emailField.fieldName] || p.data[emailField.id])
        : null;
    const isValidEmail = !!participantEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(participantEmail).trim());

    useEffect(() => {
        if (!open) return;
        const handle = (e) => {
            if (
                btnRef.current && !btnRef.current.contains(e.target) &&
                dropRef.current && !dropRef.current.contains(e.target)
            ) setOpen(false);
        };
        document.addEventListener('mousedown', handle);
        return () => document.removeEventListener('mousedown', handle);
    }, [open]);

    const handleOpen = () => {
        if (btnRef.current) {
            const rect = btnRef.current.getBoundingClientRect();
            setDropPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
        }
        setOpen(v => !v);
    };

    const sendEmail = async (templateId) => {
        setOpen(false);
        setSending(true);
        try {
            const res = await fetch(`${API}/api/v2/scan/resend-email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: credentials.username,
                    password: credentials.password,
                    participantId: p._id,
                    templateId
                })
            });
            const data = await res.json();
            if (data.success) {
                toast.success('Email sent');
            } else {
                toast.error(data.message || 'Failed to send email');
            }
        } catch {
            toast.error('Cannot connect to server');
        } finally {
            setSending(false);
        }
    };

    const options = emailTemplates?.length > 0
        ? emailTemplates
        : [{ id: 'default', name: 'Default (Registration Confirmation)' }];

    if (!isValidEmail) {
        return (
            <button disabled
                title={participantEmail ? `Invalid email: ${participantEmail}` : 'No email on file'}
                className="flex items-center gap-1.5 px-2 py-1 rounded-md text-gray-300 cursor-not-allowed">
                <Mail className="w-3.5 h-3.5 shrink-0" />
                <span className="text-[11px] font-semibold">Resend</span>
            </button>
        );
    }

    return (
        <>
            <button
                ref={btnRef}
                onClick={() => !sending && handleOpen()}
                disabled={sending}
                title={`Resend email to ${participantEmail}`}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors ${
                    open ? 'bg-indigo-50 text-indigo-600' : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'
                }`}>
                {sending
                    ? <RefreshCw className="w-3.5 h-3.5 animate-spin text-indigo-500 shrink-0" />
                    : <Mail className="w-3.5 h-3.5 shrink-0" />}
                <span className="text-[11px] font-semibold">{sending ? 'Sending...' : 'Resend'}</span>
            </button>
            {open && (
                <div
                    ref={dropRef}
                    style={{ position: 'fixed', top: dropPos.top, right: dropPos.right, zIndex: 9999 }}
                    className="bg-white border border-gray-200 rounded-lg shadow-xl min-w-[210px] py-1">
                    <p className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100">
                        Select template
                    </p>
                    {options.map(t => (
                        <button key={t.id} onClick={() => sendEmail(t.id)}
                            className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors flex items-center gap-2">
                            <Mail className="w-3 h-3 text-gray-400 shrink-0" />
                            {t.name}
                        </button>
                    ))}
                </div>
            )}
        </>
    );
};

// ── Participant Table Row ──────────────────────────────────────────────────────
const PRow = ({ p, fields, expanded, onToggle, serialNo, credentials, emailTemplates }) => {
    const nameKeys = ['name', 'fullName', 'full_name', 'Name', 'firstName', 'first_name'];
    let displayName = 'Unknown';
    for (const k of nameKeys) { if (p.data[k]) { displayName = p.data[k]; break; } }
    if (displayName === 'Unknown') {
        const v = Object.values(p.data).find(v => typeof v === 'string' && v.length > 1);
        if (v) displayName = v;
    }

    return (
        <>
            <tr onClick={onToggle} className="cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100">
                <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-gray-400 shrink-0 w-5 text-right select-none">{serialNo}</span>
                        <span className="text-sm font-semibold text-gray-800 truncate max-w-[160px]">{displayName}</span>
                    </div>
                </td>
                <td className="px-4 py-2.5 text-[11px] font-mono text-gray-400 hidden sm:table-cell">{p.registrationId || '—'}</td>
                <td className="px-4 py-2.5 hidden md:table-cell">
                    {p.categoryName
                        ? <span className="px-2 py-0.5 text-[10px] font-bold bg-indigo-50 text-indigo-600 rounded-md">{p.categoryName}</span>
                        : <span className="text-gray-300 text-[10px]">—</span>}
                </td>
                <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-md ${
                        p.paymentStatus === 'completed' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                    }`}>{p.paymentStatus}</span>
                </td>
                <td className="px-4 py-2.5 text-gray-300">
                    <ChevronRight className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                </td>
                <td className="px-2 py-2.5" onClick={e => e.stopPropagation()}>
                    <ResendMailCell p={p} fields={fields} credentials={credentials} emailTemplates={emailTemplates} />
                </td>
            </tr>
            {expanded && (
                <tr className="bg-gray-50 border-b border-gray-100">
                    <td colSpan={6} className="px-6 py-3">
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2">
                            {fields.length > 0
                                ? fields.map(f => {
                                    const val = p.data[f.id] || p.data[f.fieldName];
                                    if (!val) return null;
                                    return (
                                        <div key={f.id}>
                                            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">{f.fieldName}</p>
                                            <p className="text-xs text-gray-700 mt-0.5">{String(val)}</p>
                                        </div>
                                    );
                                })
                                : Object.entries(p.data).map(([k, v]) => (
                                    <div key={k}>
                                        <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">{k}</p>
                                        <p className="text-xs text-gray-700 mt-0.5">{String(v)}</p>
                                    </div>
                                ))}
                        </div>
                        {p.isCheckedIn && p.checkedInAt && (
                            <p className="text-[10px] text-emerald-600 mt-2 flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" />
                                Checked in at {new Date(p.checkedInAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                        )}
                    </td>
                </tr>
            )}
        </>
    );
};

// ── Participant Table ──────────────────────────────────────────────────────────
const ParticipantTable = ({ participants, fields, emailTemplates, credentials, serialOffset = 0 }) => {
    const [expandedId, setExpandedId] = useState(null);
    return (
        <div className="overflow-x-auto rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                        <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Name</th>
                        <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider hidden sm:table-cell">Reg ID</th>
                        <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider hidden md:table-cell">Category</th>
                        <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-2.5 w-8"></th>
                        <th className="px-2 py-2.5 w-8"></th>
                    </tr>
                </thead>
                <tbody className="bg-white">
                    {participants.map((p, idx) => (
                        <PRow key={p._id} p={p} fields={fields}
                            expanded={expandedId === p._id}
                            onToggle={() => setExpandedId(expandedId === p._id ? null : p._id)}
                            serialNo={serialOffset + idx + 1}
                            credentials={credentials}
                            emailTemplates={emailTemplates} />
                    ))}
                    {participants.length === 0 && (
                        <tr><td colSpan={6} className="text-center py-10 text-gray-400 text-sm">No participants found</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

// ── Pagination ─────────────────────────────────────────────────────────────────
const Pagination = ({ page, pageSize, total, onPage, onPageSize }) => {
    const totalPages = Math.ceil(total / pageSize);
    if (total === 0) return null;
    const from = Math.min((page - 1) * pageSize + 1, total);
    const to = Math.min(page * pageSize, total);
    return (
        <div className="flex items-center justify-between gap-3 pt-1">
            <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-400">Rows per page:</span>
                <div className="flex gap-0.5 bg-gray-100 border border-gray-200 rounded-lg p-0.5">
                    {[50, 100].map(n => (
                        <button key={n} onClick={() => { onPageSize(n); onPage(1); }}
                            className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${
                                pageSize === n ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                            }`}>{n}</button>
                    ))}
                </div>
            </div>
            <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400 font-mono">{from}–{to} of {total}</span>
                <div className="flex gap-0.5">
                    <button onClick={() => onPage(page - 1)} disabled={page === 1}
                        className="p-1.5 rounded-md border border-gray-200 text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors bg-white">
                        <ChevronLeft className="w-3.5 h-3.5" />
                    </button>
                    <span className="flex items-center px-2.5 text-xs font-semibold text-gray-600 bg-white border border-gray-200 rounded-md">
                        {page} / {totalPages}
                    </span>
                    <button onClick={() => onPage(page + 1)} disabled={page >= totalPages}
                        className="p-1.5 rounded-md border border-gray-200 text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors bg-white">
                        <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── All Participants Tab ───────────────────────────────────────────────────────
const AllTab = ({ credentials }) => {
    const [raw, setRaw] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [checkinFilter, setCheckinFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    useEffect(() => { setPage(1); }, [search, checkinFilter]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API}/api/v2/scan/participants`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(credentials)
            });
            const json = await res.json();
            if (json.success) setRaw(json);
        } catch {}
        finally { setLoading(false); }
    }, [credentials]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <div className="flex justify-center py-32"><RefreshCw className="w-6 h-6 text-indigo-500 animate-spin" /></div>;
    if (!raw) return null;

    const filtered = raw.data.filter(p => {
        if (checkinFilter === 'in' && !p.isCheckedIn) return false;
        if (checkinFilter === 'out' && p.isCheckedIn) return false;
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return Object.values(p.data).join(' ').toLowerCase().includes(q)
            || (p.registrationId || '').toLowerCase().includes(q)
            || (p.categoryName || '').toLowerCase().includes(q);
    });
    const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-2 items-center">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input value={search} onChange={e => setSearch(e.target.value)}
                        placeholder="Search name, reg ID, category..."
                        className="w-full pl-9 pr-8 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    {search && (
                        <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
                <div className="flex gap-0.5 bg-gray-100 border border-gray-200 rounded-lg p-0.5">
                    {[['all', 'All'], ['in', 'Checked In'], ['out', 'Pending']].map(([v, l]) => (
                        <button key={v} onClick={() => setCheckinFilter(v)}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                                checkinFilter === v ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                            }`}>{l}</button>
                    ))}
                </div>
                <button onClick={load} className="p-2 bg-white border border-gray-200 rounded-lg text-gray-400 hover:text-gray-600 transition-colors">
                    <RefreshCw className="w-4 h-4" />
                </button>
                <span className="text-xs text-gray-400 font-mono">{filtered.length} results</span>
            </div>

            <ParticipantTable participants={paginated} fields={raw.participantFields}
                emailTemplates={raw.emailTemplates || []} credentials={credentials}
                serialOffset={(page - 1) * pageSize} />

            <Pagination page={page} pageSize={pageSize} total={filtered.length}
                onPage={setPage} onPageSize={setPageSize} />
        </div>
    );
};

// ── By Category Tab ────────────────────────────────────────────────────────────
const ByCatTab = ({ credentials }) => {
    const [raw, setRaw] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeCat, setActiveCat] = useState(null);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    useEffect(() => { setPage(1); }, [search, activeCat]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API}/api/v2/scan/participants`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(credentials)
            });
            const json = await res.json();
            if (json.success) {
                setRaw(json);
                if (json.participantCategories.length > 0 && !activeCat)
                    setActiveCat(json.participantCategories[0].id);
            }
        } catch {}
        finally { setLoading(false); }
    }, [credentials]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    if (loading) return <div className="flex justify-center py-32"><RefreshCw className="w-6 h-6 text-indigo-500 animate-spin" /></div>;
    if (!raw) return null;
    if (raw.participantCategories.length === 0)
        return <p className="text-center text-gray-400 py-20">No categories configured for this event</p>;

    const grouped = {};
    for (const cat of raw.participantCategories) grouped[cat.id] = [];
    grouped['__none__'] = [];
    for (const p of raw.data) {
        const key = p.categoryId && grouped[p.categoryId] !== undefined ? p.categoryId : '__none__';
        grouped[key].push(p);
    }

    const allTabs = [
        ...raw.participantCategories,
        ...(grouped['__none__'].length > 0 ? [{ id: '__none__', name: 'Uncategorized' }] : [])
    ];

    const catParticipants = grouped[activeCat] || [];
    const filtered = catParticipants.filter(p => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return Object.values(p.data).join(' ').toLowerCase().includes(q)
            || (p.registrationId || '').toLowerCase().includes(q);
    });
    const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);
    const checkedCount = catParticipants.filter(p => p.isCheckedIn).length;

    return (
        <div className="space-y-4">
            {/* Category pills */}
            <div className="flex gap-1.5 overflow-x-auto pb-1">
                {allTabs.map(cat => {
                    const g = grouped[cat.id] || [];
                    const cin = g.filter(p => p.isCheckedIn).length;
                    const active = activeCat === cat.id;
                    return (
                        <button key={cat.id}
                            onClick={() => { setActiveCat(cat.id); setSearch(''); }}
                            className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                                active
                                    ? 'bg-indigo-600 border-indigo-600 text-white'
                                    : 'bg-white border-gray-200 text-gray-600 hover:border-indigo-300 hover:text-indigo-600'
                            }`}>
                            {cat.name}
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${
                                active ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
                            }`}>{cin}/{g.length}</span>
                        </button>
                    );
                })}
            </div>

            {/* Mini KPIs */}
            <div className="grid grid-cols-3 gap-3">
                <KPI label="Registered" value={catParticipants.length} icon={Users} color="indigo" />
                <KPI label="Checked In" value={checkedCount} icon={CheckCircle2} color="green" />
                <KPI label="Pending" value={catParticipants.length - checkedCount} icon={Clock} color="amber" />
            </div>

            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Search in this category..."
                    className="w-full pl-9 pr-8 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                {search && (
                    <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        <X className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>

            <ParticipantTable participants={paginated} fields={raw.participantFields}
                emailTemplates={raw.emailTemplates || []} credentials={credentials}
                serialOffset={(page - 1) * pageSize} />

            <Pagination page={page} pageSize={pageSize} total={filtered.length}
                onPage={setPage} onPageSize={setPageSize} />
        </div>
    );
};

// ── Bulk Edit Tab ─────────────────────────────────────────────────────────────
const FixTab = ({ credentials }) => {
    const [step, setStep] = useState('upload');      // upload | map | result
    const [dbFields, setDbFields] = useState([]);
    const [excelRows, setExcelRows] = useState([]);
    const [excelCols, setExcelCols] = useState([]);
    const [colMapping, setColMapping] = useState([]); // [{excelCol, dbFieldId, isMatchKey}]
    const [applying, setApplying] = useState(false);
    const [result, setResult] = useState(null); // { updated, notFound, total }
    const fileInputRef = useRef(null);

    // Load event fields once on mount
    const loadFields = useCallback(async () => {
        try {
            const res = await fetch(`${API}/api/v2/scan/participants`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(credentials)
            });
            const json = await res.json();
            if (json.success) setDbFields(json.participantFields || []);
        } catch {}
    }, [credentials]);

    useEffect(() => { loadFields(); }, [loadFields]);

    const handleFile = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const wb = XLSX.read(evt.target.result, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
                if (!rows.length) { toast.error('Excel file is empty'); return; }

                const cols = Object.keys(rows[0]);
                setExcelRows(rows);
                setExcelCols(cols);

                // Auto-map: match Excel column name to DB field by name similarity
                const mapped = cols.map((col, i) => {
                    const match = dbFields.find(f =>
                        f.fieldName.toLowerCase().replace(/\s/g, '') === col.toLowerCase().replace(/\s/g, '')
                    );
                    return { excelCol: col, dbFieldId: match?.id || '', isMatchKey: i === 0 };
                });
                // Prefer auto-match: set match key to first auto-matched column
                const firstMatched = mapped.findIndex(m => m.dbFieldId);
                if (firstMatched >= 0) mapped.forEach((m, i) => { m.isMatchKey = i === firstMatched; });

                setColMapping(mapped);
                setResult(null);
                setStep('map');
            } catch { toast.error('Failed to parse Excel file'); }
        };
        reader.readAsBinaryString(file);
        e.target.value = '';
    };

    const setMatchKey = (excelCol) =>
        setColMapping(prev => prev.map(m => ({ ...m, isMatchKey: m.excelCol === excelCol })));

    const setFieldId = (excelCol, dbFieldId) =>
        setColMapping(prev => prev.map(m => m.excelCol === excelCol ? { ...m, dbFieldId } : m));

    const matchCol = colMapping.find(m => m.isMatchKey);
    const canApply = matchCol?.dbFieldId && colMapping.some(m => m.dbFieldId);

    const apply = async () => {
        if (!canApply) return;
        setApplying(true);
        try {
            const res = await fetch(`${API}/api/v2/scan/bulk-edit`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: credentials.username,
                    password: credentials.password,
                    rows: excelRows,
                    columnMapping: colMapping.filter(m => m.dbFieldId)
                })
            });
            const data = await res.json();
            if (data.success) {
                setResult(data);
                setStep('result');
                toast.success(`Updated ${data.updated} of ${data.total} rows`);
            } else {
                toast.error(data.message || 'Failed');
            }
        } catch { toast.error('Cannot connect to server'); }
        finally { setApplying(false); }
    };

    // ── Step: Upload ──
    if (step === 'upload') return (
        <div className="flex flex-col items-center justify-center py-20">
            <div
                onClick={() => fileInputRef.current?.click()}
                className="w-full max-w-md border-2 border-dashed border-gray-300 hover:border-indigo-400 rounded-2xl p-12 flex flex-col items-center gap-4 cursor-pointer transition-colors group">
                <div className="w-14 h-14 bg-indigo-50 rounded-xl flex items-center justify-center group-hover:bg-indigo-100 transition-colors">
                    <ArrowLeftRight className="w-6 h-6 text-indigo-500" />
                </div>
                <div className="text-center">
                    <p className="font-bold text-gray-800 text-sm">Upload corrected Excel</p>
                    <p className="text-xs text-gray-400 mt-1">Click to browse · .xlsx or .xls</p>
                </div>
            </div>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFile} />
            <p className="text-xs text-gray-400 mt-6 text-center max-w-sm">
                The system will match each row by the column you choose, then update all mapped fields.
            </p>
        </div>
    );

    // ── Step: Map ──
    if (step === 'map') return (
        <div className="space-y-4 pb-6">

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm font-bold text-gray-800">{excelRows.length} rows detected</p>
                    <p className="text-xs text-gray-400 mt-0.5">{excelCols.length} columns found in Excel</p>
                </div>
                <button onClick={() => { setStep('upload'); setExcelRows([]); setExcelCols([]); }}
                    className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors">
                    <X className="w-3.5 h-3.5" /> Change file
                </button>
            </div>

            {/* Mapping table */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <p className="text-xs font-bold text-gray-600 uppercase tracking-wider">Column Mapping</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                        Map each Excel column to its DB field · tick <strong>Match</strong> on the column used to find existing participants
                    </p>
                </div>
                <table className="w-full text-xs">
                    <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                            <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-10 text-center">Match</th>
                            <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Excel Column</th>
                            <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider hidden sm:table-cell">Sample values</th>
                            <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Maps to DB Field</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {colMapping.map(m => {
                            const samples = excelRows.slice(0, 3).map(r => String(r[m.excelCol] || '')).filter(Boolean);
                            return (
                                <tr key={m.excelCol} className={m.isMatchKey ? 'bg-indigo-50/40' : 'bg-white'}>
                                    <td className="px-4 py-3 text-center">
                                        <input type="radio" name="matchKey" checked={m.isMatchKey}
                                            onChange={() => setMatchKey(m.excelCol)}
                                            className="w-3.5 h-3.5 accent-indigo-600 cursor-pointer" />
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`font-semibold ${m.isMatchKey ? 'text-indigo-700' : 'text-gray-800'}`}>
                                            {m.excelCol}
                                        </span>
                                        {m.isMatchKey && (
                                            <span className="ml-2 text-[9px] font-bold text-indigo-600 bg-indigo-100 px-1.5 py-0.5 rounded uppercase tracking-wide">
                                                match key
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 hidden sm:table-cell">
                                        <span className="text-gray-400 truncate block max-w-[200px]">
                                            {samples.slice(0, 2).join(' · ') || <span className="italic">empty</span>}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <select value={m.dbFieldId} onChange={e => setFieldId(m.excelCol, e.target.value)}
                                            className="w-full py-1.5 px-2.5 text-xs border border-gray-300 rounded-lg bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                                            <option value="">— Skip this column —</option>
                                            {dbFields.map(f => (
                                                <option key={f.id} value={f.id}>{f.fieldName}</option>
                                            ))}
                                        </select>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Apply bar */}
            <div className="flex items-center justify-between gap-3 bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 sticky bottom-3">
                <p className="text-xs text-gray-500">
                    {!matchCol?.dbFieldId
                        ? <span className="text-amber-600 font-medium">Select a match column (radio button) and map it to a DB field</span>
                        : <>Match by <span className="font-bold text-indigo-700">{matchCol.excelCol}</span> · updating <span className="font-bold text-gray-800">{colMapping.filter(m => m.dbFieldId).length}</span> field{colMapping.filter(m => m.dbFieldId).length !== 1 ? 's' : ''} across <span className="font-bold text-gray-800">{excelRows.length}</span> rows</>
                    }
                </p>
                <button onClick={apply} disabled={applying || !canApply}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm">
                    {applying
                        ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Updating...</>
                        : <><ArrowLeftRight className="w-3.5 h-3.5" /> Update {excelRows.length} rows</>
                    }
                </button>
            </div>
        </div>
    );

    // ── Step: Result ──
    return (
        <div className="flex flex-col items-center justify-center py-20 gap-6">
            <div className="w-full max-w-sm bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center">
                <div className="w-14 h-14 bg-emerald-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-7 h-7 text-emerald-500" />
                </div>
                <p className="text-2xl font-black text-gray-900">{result?.updated}</p>
                <p className="text-sm text-gray-500 mt-1">participants updated</p>
                {result?.notFound?.length > 0 && (
                    <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-left">
                        <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-1.5">
                            {result.notFound.length} rows not matched
                        </p>
                        <div className="space-y-0.5 max-h-32 overflow-y-auto">
                            {result.notFound.map((v, i) => (
                                <p key={i} className="text-[11px] text-amber-800 font-mono truncate">{v || '(empty)'}</p>
                            ))}
                        </div>
                    </div>
                )}
                <p className="text-[10px] text-gray-400 mt-4">{result?.total} rows in file · {result?.updated} matched &amp; updated</p>
            </div>
            <button onClick={() => { setStep('upload'); setExcelRows([]); setExcelCols([]); setResult(null); }}
                className="flex items-center gap-2 text-xs text-indigo-600 hover:text-indigo-800 font-semibold transition-colors">
                <RefreshCw className="w-3.5 h-3.5" /> Upload another file
            </button>
        </div>
    );
};

// ── Main ───────────────────────────────────────────────────────────────────────
const TABS = [
    { id: 'overview',  label: 'Overview',     Icon: BarChart2      },
    { id: 'all',       label: 'Participants',  Icon: List           },
    { id: 'category',  label: 'By Category',  Icon: Layers         },
    { id: 'fix',       label: 'Bulk Edit',     Icon: ArrowLeftRight },
];

const EventDashboardV2 = () => {
    const [auth, setAuth] = useState(null);
    const [tab, setTab] = useState('overview');
    const [navStats, setNavStats] = useState(null);
    const [showEmpty, setShowEmpty] = useState(false);

    useEffect(() => {
        try { const s = localStorage.getItem(STORAGE_KEY); if (s) setAuth(JSON.parse(s)); } catch {}
    }, []);

    if (!auth) return <LoginScreen onLogin={a => setAuth(a)} />;

    const creds = { username: auth.username, password: auth.password };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center shrink-0">
                            <LayoutDashboard className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <span className="font-bold text-sm text-gray-900">{auth.eventInfo?.name || 'Event'}</span>
                            <span className="ml-2 text-[10px] text-gray-400">@{auth.username}</span>
                        </div>
                        {navStats && (
                            <div className="hidden sm:flex items-center gap-1.5 ml-2 bg-indigo-50 border border-indigo-100 px-3 py-1 rounded-lg">
                                <Users className="w-3 h-3 text-indigo-400" />
                                <span className="text-xs font-black text-indigo-700">{navStats.total}</span>
                                <span className="text-[10px] text-indigo-400 font-medium">registered</span>
                            </div>
                        )}
                    </div>

                    {/* Desktop tabs */}
                    <nav className="hidden sm:flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                        {TABS.map(({ id, label, Icon }) => (
                            <button key={id} onClick={() => setTab(id)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                                    tab === id
                                        ? 'bg-white text-indigo-600 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}>
                                <Icon className="w-3.5 h-3.5" />{label}
                            </button>
                        ))}
                    </nav>

                    {tab === 'overview' && (
                        <div className="hidden sm:flex items-center gap-2">
                            <span className="text-xs text-gray-400 font-medium">Show all</span>
                            <button
                                onClick={() => setShowEmpty(v => !v)}
                                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${showEmpty ? 'bg-indigo-600' : 'bg-gray-200'}`}
                            >
                                <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform duration-200 ${showEmpty ? 'translate-x-4' : 'translate-x-0'}`} />
                            </button>
                        </div>
                    )}

                    <button
                        onClick={() => { localStorage.removeItem(STORAGE_KEY); setAuth(null); }}
                        className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors">
                        <LogOut className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Logout</span>
                    </button>
                </div>

                {/* Mobile tab bar */}
                <div className="sm:hidden flex border-t border-gray-100">
                    {TABS.map(({ id, label, Icon }) => (
                        <button key={id} onClick={() => setTab(id)}
                            className={`flex-1 flex flex-col items-center gap-0.5 py-2 text-[10px] font-semibold transition-colors ${
                                tab === id
                                    ? 'text-indigo-600 border-t-2 border-indigo-500 -mt-px bg-indigo-50/50'
                                    : 'text-gray-400'
                            }`}>
                            <Icon className="w-4 h-4" />{label}
                        </button>
                    ))}
                </div>
            </header>

            {/* Content — fixed to viewport height, no page scroll */}
            <main className="px-4 py-3 overflow-hidden" style={{ height: 'calc(100vh - 56px)' }}>
                {tab === 'overview' && <OverviewTab credentials={creds} onStats={setNavStats} showEmpty={showEmpty} />}
                {tab === 'all'      && <div className="h-full overflow-y-auto"><AllTab credentials={creds} /></div>}
                {tab === 'category' && <div className="h-full overflow-y-auto"><ByCatTab credentials={creds} /></div>}
                {tab === 'fix'      && <div className="h-full overflow-y-auto"><FixTab credentials={creds} /></div>}
            </main>
        </div>
    );
};

export default EventDashboardV2;
